import { Navigate } from "react-router-dom";
import { usePermissionsV2 } from "../hooks/usePermissionsV2";
import { Center, Loader, Stack, Title, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";

/**
 * Enhanced Protected Route component with v2 permission support
 * @param {ReactNode} children - The component to render if authorized
 * @param {string|string[]} permissions - Required permission(s) to access the route
 * @param {string} permissionMode - 'any' or 'all' when multiple permissions provided
 * @param {ReactNode} fallback - Component to show when access denied
 * @param {string} redirectTo - Path to redirect if not authenticated
 */
export const ProtectedRouteV2 = ({ 
  children, 
  permissions = null,
  permissionMode = 'any',
  fallback = null,
  redirectTo = "/login"
}) => {
  const perms = usePermissionsV2();
  
  // Check if user is authenticated
  if (!perms.user) {
    return <Navigate to={redirectTo} replace />;
  }
  
  // If still loading permission data, show loader
  if (!perms.staffProfile) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }
  
  // If no specific permissions required, just check authentication
  if (!permissions) {
    return children;
  }
  
  // Check permissions
  const permissionArray = Array.isArray(permissions) ? permissions : [permissions];
  const hasAccess = permissionMode === 'all' 
    ? perms.canAll(permissionArray)
    : perms.canAny(permissionArray);
  
  if (!hasAccess) {
    // Show custom fallback or default permission denied
    if (fallback) {
      return fallback;
    }
    
    return (
      <Center h="100vh">
        <Stack align="center" spacing="md">
          <IconLock size={72} color="#868e96" />
          <Title order={3} color="dimmed">Access Denied</Title>
          <Text color="dimmed" align="center" maw={400}>
            You don't have permission to access this page.
          </Text>
          <Text size="sm" color="dimmed">
            Required: {permissionArray.join(', ')}
          </Text>
        </Stack>
      </Center>
    );
  }
  
  return children;
};